import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { ConfirmDialog } from '../components/ConfirmDialog';

const DAYS = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 7, label: 'Sunday' },
];

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-background)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
  marginBottom: 'var(--spacing-1)',
};

const primaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'var(--color-accent)',
  border: 'none',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text-inverse)',
  fontWeight: 600,
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontWeight: 500,
};

const offBadgeStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  padding: '2px 8px',
  borderRadius: '999px',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text-muted)',
  backgroundColor: 'var(--color-background)',
};

type ReminderFlag = 'sms_rebooking_reminders_enabled' | 'sms_appointment_reminders_enabled';

const REMINDER_SWITCHES: { flag: ReminderFlag; label: string; description: string }[] = [
  {
    flag: 'sms_rebooking_reminders_enabled',
    label: 'Rebooking reminders',
    description:
      '"Time for your next cut" SMS, sent daily at 1 pm to customers whose most recent visit was here.',
  },
  {
    flag: 'sms_appointment_reminders_enabled',
    label: 'Appointment reminders',
    description: 'Day-before SMS for appointments booked at this location.',
  },
];

function defaultHours() {
  return DAYS.map((day) => ({
    day_of_week: day.value,
    open_time: day.value === 7 ? '00:00' : '09:00',
    close_time: day.value === 7 ? '00:00' : '17:00',
    closed: day.value === 7,
  }));
}

export function Locations() {
  const queryClient = useQueryClient();
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [locationForm, setLocationForm] = useState({ name: '', timezone: 'Australia/Brisbane' });
  const [selectedLocationForm, setSelectedLocationForm] = useState({
    name: '',
    timezone: 'Australia/Brisbane',
  });
  const [hoursForm, setHoursForm] = useState(defaultHours);
  const [onlineConfirmOpen, setOnlineConfirmOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState<string | null>(null);
  // Which SMS-reminder switch is awaiting confirmation (migration 053).
  const [reminderConfirm, setReminderConfirm] = useState<{
    flag: ReminderFlag;
    enable: boolean;
  } | null>(null);

  // This page is the control panel, so it lists EVERY location — including
  // ones switched off, which are hidden from every other screen.
  const { data: locations = [] } = useQuery({
    queryKey: ['locations', 'all'],
    queryFn: api.getAllLocations,
  });
  const { data: openingHours = [] } = useQuery({
    queryKey: ['opening-hours'],
    queryFn: () => api.getOpeningHours(),
  });

  const selectedLocation = useMemo(
    () => locations.find((location: any) => location.id === selectedLocationId) || null,
    [locations, selectedLocationId],
  );

  const createLocationMutation = useMutation({
    mutationFn: api.createLocation,
    onSuccess: (location) => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setSelectedLocationId(location.id);
      setLocationForm({ name: '', timezone: 'Australia/Brisbane' });
    },
  });

  const updateLocationMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => api.updateLocation(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['locations'] }),
  });

  const updateHoursMutation = useMutation({
    mutationFn: ({ id, hours }: { id: number; hours: any[] }) => api.replaceOpeningHours(id, hours),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['opening-hours'] }),
  });

  // Online-bookings switch (migration 052). Off = hidden from the booking
  // website and customer bookings rejected; the admin calendar, rosters and
  // existing appointments are untouched.
  const onlineBookingsMutation = useMutation({
    mutationFn: ({ id, accepting }: { id: number; accepting: boolean }) =>
      api.setLocationOnlineBookings(id, accepting),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['locations'] }),
  });

  // SMS reminder switches (migration 053) — rebooking nudges and day-before
  // appointment reminders, per location. Booking confirmations are never
  // affected.
  const reminderMutation = useMutation({
    mutationFn: ({ id, flag, enable }: { id: number; flag: ReminderFlag; enable: boolean }) =>
      api.updateLocation(id, { [flag]: enable }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['locations'] }),
  });

  const syncHoursFormFromLocation = (locationId: number) => {
    setSelectedLocationId(locationId);
    const targetLocation = locations.find((location: any) => location.id === locationId);
    setSelectedLocationForm({
      name: targetLocation?.name || '',
      timezone: targetLocation?.timezone || 'Australia/Brisbane',
    });
    const locationHours = openingHours
      .filter((hour: any) => hour.location_id === locationId)
      .sort((a: any, b: any) => a.day_of_week - b.day_of_week);

    if (locationHours.length === 0) {
      setHoursForm(defaultHours());
      return;
    }

    setHoursForm(
      DAYS.map((day) => {
        const match = locationHours.find((hour: any) => hour.day_of_week === day.value);
        return {
          day_of_week: day.value,
          open_time: match?.open_time?.slice(0, 5) || '09:00',
          close_time: match?.close_time?.slice(0, 5) || '17:00',
          closed: !match,
        };
      }),
    );
  };

  useEffect(() => {
    if (!selectedLocationId && locations.length > 0) {
      syncHoursFormFromLocation(locations[0].id);
    }
  }, [locations, selectedLocationId]);

  const acceptingOnline = selectedLocation
    ? selectedLocation.accepting_online_bookings !== false
    : true;

  async function toggleOnlineBookings() {
    setOnlineConfirmOpen(false);
    if (!selectedLocation) return;
    await onlineBookingsMutation.mutateAsync({
      id: selectedLocation.id,
      accepting: !acceptingOnline,
    });
  }

  const reminderOn = (flag: ReminderFlag) =>
    selectedLocation ? selectedLocation[flag] !== false : true;

  function requestReminderToggle(flag: ReminderFlag) {
    if (!selectedLocation) return;
    const enable = !reminderOn(flag);
    // Turning a reminder back ON is harmless — apply straight away. Turning
    // one OFF stops messages to real customers, so confirm first.
    if (enable) {
      reminderMutation.mutate({ id: selectedLocation.id, flag, enable });
    } else {
      setReminderConfirm({ flag, enable });
    }
  }

  async function applyReminderToggle() {
    const pending = reminderConfirm;
    setReminderConfirm(null);
    if (!pending || !selectedLocation) return;
    await reminderMutation.mutateAsync({ id: selectedLocation.id, ...pending });
  }

  async function exportFutureBookings() {
    if (!selectedLocation) return;
    setExporting(true);
    setExportMessage(null);
    try {
      const { blob, filename } = await api.exportLocationBookings(selectedLocation.id);
      const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Brisbane' });
      const fallback = `${String(selectedLocation.name)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')}-bookings-from-${today}.csv`;
      const name = filename || fallback;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = name;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setExportMessage(`Downloaded ${name}`);
    } catch (err) {
      setExportMessage(`Export failed: ${(err as Error).message}`);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="locations-page" style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
      <div>
        <h1>Locations</h1>
        <p style={{ color: 'var(--color-text-muted)', marginTop: 'var(--spacing-2)' }}>
          Create locations and manage the available opening hours used by the booking and roster
          screens.
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(320px, 0.9fr) minmax(420px, 1.1fr)',
          gap: 'var(--spacing-4)',
        }}
      >
        <section style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
          <div style={cardStyle}>
            <h2 style={{ marginBottom: 'var(--spacing-3)' }}>Create Location</h2>
            <div style={{ display: 'grid', gap: 'var(--spacing-3)' }}>
              <div>
                <label style={labelStyle}>Location Name</label>
                <input
                  value={locationForm.name}
                  onChange={(e) =>
                    setLocationForm((current) => ({ ...current, name: e.target.value }))
                  }
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Timezone</label>
                <input
                  value={locationForm.timezone}
                  onChange={(e) =>
                    setLocationForm((current) => ({ ...current, timezone: e.target.value }))
                  }
                  style={inputStyle}
                />
              </div>
              <button
                onClick={() => createLocationMutation.mutate(locationForm)}
                disabled={createLocationMutation.isPending || !locationForm.name.trim()}
                style={{ ...primaryButtonStyle, opacity: !locationForm.name.trim() ? 0.6 : 1 }}
              >
                {createLocationMutation.isPending ? 'Saving...' : 'Add Location'}
              </button>
            </div>
          </div>

          <div style={cardStyle}>
            <h2 style={{ marginBottom: 'var(--spacing-3)' }}>Locations</h2>
            <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
              {locations.map((location: any) => (
                <button
                  key={location.id}
                  onClick={() => syncHoursFormFromLocation(location.id)}
                  style={{
                    ...secondaryButtonStyle,
                    justifyContent: 'space-between',
                    display: 'flex',
                    backgroundColor:
                      selectedLocationId === location.id
                        ? 'rgba(217, 178, 86, 0.14)'
                        : 'var(--color-surface-elevated)',
                    borderColor:
                      selectedLocationId === location.id
                        ? 'rgba(217, 178, 86, 0.4)'
                        : 'var(--color-border)',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                    {location.name}
                    {location.accepting_online_bookings === false && (
                      <span style={offBadgeStyle}>Disabled</span>
                    )}
                  </span>
                  <span
                    style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}
                  >
                    {location.timezone}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section style={{ display: 'grid', gap: 'var(--spacing-4)', alignContent: 'start' }}>
          <div style={cardStyle}>
            <h2 style={{ marginBottom: 'var(--spacing-1)' }}>Location status</h2>
            {!selectedLocation ? (
              <p style={{ color: 'var(--color-text-muted)' }}>Select a location on the left.</p>
            ) : (
              <>
                <p style={{ color: 'var(--color-text-muted)', marginBottom: 'var(--spacing-3)' }}>
                  {acceptingOnline
                    ? `${selectedLocation.name} is enabled: customers can book it on the website and it appears on the calendar, rosters, reports and every other screen.`
                    : `${selectedLocation.name} is disabled: hidden from the booking website AND from every other screen — calendar, rosters, expenses, reports. Its history is kept and this page can switch it back on.`}
                </p>
                <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setOnlineConfirmOpen(true)}
                    disabled={onlineBookingsMutation.isPending}
                    style={acceptingOnline ? secondaryButtonStyle : primaryButtonStyle}
                  >
                    {onlineBookingsMutation.isPending
                      ? 'Saving...'
                      : acceptingOnline
                        ? 'Disable location'
                        : 'Enable location'}
                  </button>
                  <button
                    onClick={exportFutureBookings}
                    disabled={exporting}
                    style={secondaryButtonStyle}
                  >
                    {exporting ? 'Preparing CSV...' : 'Export future bookings (CSV)'}
                  </button>
                </div>
                {exportMessage && (
                  <p
                    role="status"
                    style={{
                      marginTop: 'var(--spacing-2)',
                      fontSize: 'var(--font-size-sm)',
                      color: exportMessage.startsWith('Export failed')
                        ? 'var(--color-error)'
                        : 'var(--color-text-muted)',
                    }}
                  >
                    {exportMessage}
                  </p>
                )}
              </>
            )}
          </div>

          <div style={cardStyle}>
            <h2 style={{ marginBottom: 'var(--spacing-1)' }}>SMS reminders</h2>
            {!selectedLocation ? (
              <p style={{ color: 'var(--color-text-muted)' }}>Select a location on the left.</p>
            ) : (
              <>
                <p style={{ color: 'var(--color-text-muted)', marginBottom: 'var(--spacing-3)' }}>
                  Automated reminders for {selectedLocation.name}. Booking confirmations are always
                  sent.
                </p>
                <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
                  {REMINDER_SWITCHES.map((sw) => {
                    const on = reminderOn(sw.flag);
                    return (
                      <div
                        key={sw.flag}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 'var(--spacing-3)',
                          padding: 'var(--spacing-2) var(--spacing-3)',
                          border: '1px solid var(--color-border)',
                          borderRadius: 'var(--radius-default)',
                          backgroundColor: 'var(--color-surface-elevated)',
                        }}
                      >
                        <div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--spacing-2)',
                            }}
                          >
                            <strong>{sw.label}</strong>
                            {!on && <span style={offBadgeStyle}>Off</span>}
                          </div>
                          <div
                            style={{
                              fontSize: 'var(--font-size-sm)',
                              color: 'var(--color-text-muted)',
                            }}
                          >
                            {sw.description}
                          </div>
                        </div>
                        <button
                          onClick={() => requestReminderToggle(sw.flag)}
                          disabled={reminderMutation.isPending}
                          style={on ? secondaryButtonStyle : primaryButtonStyle}
                        >
                          {reminderMutation.isPending &&
                          reminderMutation.variables?.flag === sw.flag
                            ? 'Saving...'
                            : on
                              ? 'Turn off'
                              : 'Turn on'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <div style={cardStyle}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 'var(--spacing-3)',
                marginBottom: 'var(--spacing-3)',
              }}
            >
              <div>
                <h2 style={{ margin: 0 }}>Available Hours</h2>
                <p style={{ color: 'var(--color-text-muted)', marginTop: 'var(--spacing-1)' }}>
                  {selectedLocation
                    ? selectedLocation.name
                    : 'Choose a location to edit its hours.'}
                </p>
              </div>
            </div>

            {!selectedLocation ? (
              <p style={{ color: 'var(--color-text-muted)' }}>Select a location on the left.</p>
            ) : (
              <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 'var(--spacing-3)',
                    marginBottom: 'var(--spacing-3)',
                  }}
                >
                  <div>
                    <label style={labelStyle}>Location Name</label>
                    <input
                      value={selectedLocationForm.name}
                      onChange={(e) =>
                        setSelectedLocationForm((current) => ({ ...current, name: e.target.value }))
                      }
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Timezone</label>
                    <input
                      value={selectedLocationForm.timezone}
                      onChange={(e) =>
                        setSelectedLocationForm((current) => ({
                          ...current,
                          timezone: e.target.value,
                        }))
                      }
                      style={inputStyle}
                    />
                  </div>
                </div>
                {hoursForm.map((row, index) => (
                  <div
                    key={row.day_of_week}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1.2fr 0.7fr 0.7fr 0.5fr',
                      gap: 'var(--spacing-2)',
                      alignItems: 'center',
                      padding: 'var(--spacing-2)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-default)',
                      backgroundColor: 'var(--color-surface-elevated)',
                    }}
                  >
                    <strong>{DAYS.find((day) => day.value === row.day_of_week)?.label}</strong>
                    <input
                      type="time"
                      value={row.open_time}
                      disabled={row.closed}
                      onChange={(e) =>
                        setHoursForm((current) =>
                          current.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, open_time: e.target.value } : item,
                          ),
                        )
                      }
                      style={inputStyle}
                    />
                    <input
                      type="time"
                      value={row.close_time}
                      disabled={row.closed}
                      onChange={(e) =>
                        setHoursForm((current) =>
                          current.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, close_time: e.target.value } : item,
                          ),
                        )
                      }
                      style={inputStyle}
                    />
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: 'var(--font-size-sm)',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={row.closed}
                        onChange={(e) =>
                          setHoursForm((current) =>
                            current.map((item, itemIndex) =>
                              itemIndex === index ? { ...item, closed: e.target.checked } : item,
                            ),
                          )
                        }
                      />
                      Closed
                    </label>
                  </div>
                ))}
                <button
                  onClick={async () => {
                    await updateLocationMutation.mutateAsync({
                      id: selectedLocation.id,
                      data: selectedLocationForm,
                    });
                    await updateHoursMutation.mutateAsync({
                      id: selectedLocation.id,
                      hours: hoursForm
                        .filter((row) => !row.closed)
                        .map(({ day_of_week, open_time, close_time }) => ({
                          day_of_week,
                          open_time,
                          close_time,
                        })),
                    });
                  }}
                  disabled={updateHoursMutation.isPending || updateLocationMutation.isPending}
                  style={{ ...primaryButtonStyle, marginTop: 'var(--spacing-2)' }}
                >
                  {updateHoursMutation.isPending || updateLocationMutation.isPending
                    ? 'Saving...'
                    : 'Save Location and Hours'}
                </button>
              </div>
            )}
          </div>
        </section>
      </div>

      <ConfirmDialog
        isOpen={onlineConfirmOpen}
        title={
          acceptingOnline
            ? `Disable ${selectedLocation?.name ?? 'this location'}?`
            : `Enable ${selectedLocation?.name ?? 'this location'}?`
        }
        message={
          acceptingOnline
            ? 'The location disappears from the booking website and from every admin screen — calendar, rosters, expenses, supply requests. Any appointments and shifts it still has will no longer be visible or manageable, so move or finish them first. Nothing is deleted: its history stays in the reports and you can switch it back on here at any time.'
            : 'The location returns to the booking website and to every admin screen, with its calendar, rosters and appointments visible again.'
        }
        tone={acceptingOnline ? 'danger' : 'warning'}
        confirmLabel={acceptingOnline ? 'Disable location' : 'Enable location'}
        onConfirm={toggleOnlineBookings}
        onCancel={() => setOnlineConfirmOpen(false)}
      />

      <ConfirmDialog
        isOpen={reminderConfirm !== null}
        title={`Turn off ${REMINDER_SWITCHES.find((s) => s.flag === reminderConfirm?.flag)?.label.toLowerCase() ?? 'these reminders'} for ${selectedLocation?.name ?? 'this location'}?`}
        message={
          reminderConfirm?.flag === 'sms_rebooking_reminders_enabled'
            ? `Customers whose most recent visit was at ${selectedLocation?.name ?? 'this location'} will stop receiving the "time for your next cut" SMS from the next daily batch. Booking confirmations and appointment reminders are not affected. You can turn it back on at any time.`
            : `Customers with appointments at ${selectedLocation?.name ?? 'this location'} will stop receiving the day-before reminder SMS from the next hourly batch. Booking confirmations are not affected. You can turn it back on at any time.`
        }
        tone="danger"
        confirmLabel="Turn off"
        onConfirm={applyReminderToggle}
        onCancel={() => setReminderConfirm(null)}
      />
    </div>
  );
}
